import { useEffect } from 'react';
import { SHAPES, FLAKES } from './ButtonSparkles.js';

/**
 * GlobalSparkles — mounts once at the app root and gives EVERY `.btn-primary` /
 * `.btn-secondary` the gold-leaf sparkle layer, so buttons that aren't the
 * GildButton/MagneticButton components (plain className buttons, the connect
 * CTAs on locked pages, "Get more leaves", etc.) get the same hover effect.
 *
 * It appends a DOM `.btn-sparkles` field to any such button that doesn't already
 * have one (so the React `<ButtonSparkles/>` on the main CTAs isn't doubled),
 * and a MutationObserver keeps newly-rendered buttons covered — and re-adds the
 * layer if React reconciliation strips it. Returns no UI.
 */
const SVG_NS = 'http://www.w3.org/2000/svg';

function buildLayer(): HTMLSpanElement {
  const layer = document.createElement('span');
  layer.className = 'btn-sparkles';
  layer.setAttribute('aria-hidden', 'true');
  for (const f of FLAKES) {
    const cx = f.x - 50;
    const cy = f.y - 50;
    const len = Math.hypot(cx, cy) || 1;
    const drift = 14;
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'spark');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.style.left = `${f.x}%`;
    svg.style.top = `${f.y}%`;
    svg.style.width = `${f.s}px`;
    svg.style.height = `${f.s}px`;
    svg.style.animationDelay = `${f.d}s`;
    svg.style.setProperty('--r', `${f.r}deg`);
    svg.style.setProperty('--dx', `${((cx / len) * drift).toFixed(1)}px`);
    svg.style.setProperty('--dy', `${((cy / len) * drift).toFixed(1)}px`);
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', SHAPES[f.k] ?? '');
    svg.appendChild(path);
    layer.appendChild(svg);
  }
  return layer;
}

function ensureLayer(btn: Element): void {
  if (!(btn instanceof HTMLElement)) return;
  if (btn.querySelector(':scope > .btn-sparkles')) return; // already has one
  btn.appendChild(buildLayer());
}

export function GlobalSparkles(): null {
  useEffect(() => {
    const scan = (root: ParentNode): void => {
      root.querySelectorAll?.('.btn-primary, .btn-secondary').forEach(ensureLayer);
    };
    scan(document);

    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches('.btn-primary, .btn-secondary')) ensureLayer(n);
          scan(n);
        });
        // If React stripped our injected layer during a re-render, re-add it.
        if (m.removedNodes.length && m.target instanceof HTMLElement) {
          const btn = m.target.closest('.btn-primary, .btn-secondary');
          if (btn) ensureLayer(btn);
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  return null;
}
